import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
    Badge,
    Box,
    Button,
    Divider,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    Menu,
    Tooltip,
    Typography,
} from '@mui/material';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { useNavigate } from 'react-router-dom';
import { getDataFull, postData } from '../../services/ajax';
import { AuthContext } from '../../context/auth';
import { useSnackbar } from 'notistack';
import {
    canShow,
    closeAll,
    disable as disableBrowserNotifications,
    enable as enableBrowserNotifications,
    enqueuePending,
    flushPending,
    getPermission,
    isOptedIn,
    isSupported,
    shouldShowNow,
    showMany,
} from '../../services/browserNotifications';

const ACCION_TO_CHIP = {
    responder: 'para_responder',
    cerrar: 'cerrar_turno',
    destrabar: 'destrabar',
    insumo: 'insumos',
};

/**
 * Campana de notificaciones con polling adaptativo.
 * - Sigue pollando con pestaña oculta (para alertas nativas)
 * - Usa poll_hint_seconds del API
 * - Deep-link a /orden-practica?chip=... o /orden-practica/:id
 * - Opt-in a Notification API: se muestran al pasar a segundo plano (cola si llegan con la app enfocada)
 */
export default function NotificationBell() {
    const { usuario } = useContext(AuthContext);
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [anchorEl, setAnchorEl] = useState(null);
    const [items, setItems] = useState([]);
    const [unread, setUnread] = useState(0);
    const [pollHint, setPollHint] = useState(50);
    const [browserUi, setBrowserUi] = useState({
        supported: false,
        permission: 'denied',
        optedIn: false,
    });
    const sinceIdRef = useRef(0);
    const timerRef = useRef(null);
    const initialPollDoneRef = useRef(false);
    const onNativeClickRef = useRef(null);
    const tokenRef = useRef(usuario?.token);

    tokenRef.current = usuario?.token;

    const refreshBrowserUi = useCallback(() => {
        setBrowserUi({
            supported: isSupported(),
            permission: getPermission(),
            optedIn: isOptedIn(),
        });
    }, []);

    const goFromNotification = useCallback(async (n) => {
        setAnchorEl(null);
        const token = tokenRef.current;
        if (!n.leida_at && token) {
            await postData(`notificaciones/${n.id}/leer`, {}, token);
            setUnread((u) => Math.max(0, u - 1));
            setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, leida_at: new Date().toISOString() } : x)));
        }
        const data = n.data || {};
        if (n.tipo === 'prueba') {
            return;
        }
        if (data.orden_id) {
            navigate(`/orden-practica/${data.orden_id}`);
            return;
        }
        const chip = ACCION_TO_CHIP[data.accion] || 'todos';
        navigate(`/orden-practica?chip=${chip}`);
    }, [navigate]);

    onNativeClickRef.current = goFromNotification;

    const mergeNewItems = useCallback((list) => {
        setItems((prev) => {
            const map = new Map(prev.map((n) => [n.id, n]));
            list.forEach((n) => map.set(n.id, n));
            return Array.from(map.values()).sort((a, b) => b.id - a.id).slice(0, 20);
        });
        sinceIdRef.current = Math.max(...list.map((n) => n.id), sinceIdRef.current);
    }, []);

    const pollOnce = useCallback(async () => {
        if (!usuario?.token) return;

        const qs = initialPollDoneRef.current && sinceIdRef.current > 0
            ? `?since_id=${sinceIdRef.current}`
            : '';
        const response = await getDataFull(`notificaciones${qs}`, usuario.token);
        if (response.error) return;

        const list = Array.isArray(response.data) ? response.data : [];
        if (response.unread_count != null) setUnread(response.unread_count);
        if (response.poll_hint_seconds) setPollHint(response.poll_hint_seconds);

        const isInitial = !initialPollDoneRef.current;

        if (isInitial) {
            setItems(list);
            if (list.length) sinceIdRef.current = Math.max(...list.map((n) => n.id));
            initialPollDoneRef.current = true;
            return;
        }

        // Polls posteriores: ítems nuevos (incremental con since_id, o primer llenado tras inbox vacío)
        if (!list.length) return;

        mergeNewItems(list);
        if (shouldShowNow()) {
            showMany(list, {
                onClick: (item) => onNativeClickRef.current?.(item),
            });
        } else if (canShow()) {
            enqueuePending(list);
        }
    }, [usuario?.token, mergeNewItems]);

    useEffect(() => {
        refreshBrowserUi();
        const onVis = () => {
            if (document.visibilityState === 'hidden') {
                flushPending({
                    onClick: (item) => onNativeClickRef.current?.(item),
                });
            } else {
                closeAll();
                pollOnce();
            }
        };
        document.addEventListener('visibilitychange', onVis);
        pollOnce();
        return () => document.removeEventListener('visibilitychange', onVis);
    }, [pollOnce, refreshBrowserUi]);

    useEffect(() => {
        if (timerRef.current) window.clearInterval(timerRef.current);
        timerRef.current = window.setInterval(() => {
            pollOnce();
        }, Math.max(5, pollHint) * 1000);
        return () => {
            if (timerRef.current) window.clearInterval(timerRef.current);
        };
    }, [pollHint, pollOnce]);

    const open = Boolean(anchorEl);

    const handleOpen = (e) => {
        setAnchorEl(e.currentTarget);
        refreshBrowserUi();
        pollOnce();
    };
    const handleClose = () => setAnchorEl(null);

    const handleEnableBrowser = async () => {
        const ok = await enableBrowserNotifications();
        refreshBrowserUi();
        if (!ok && getPermission() === 'denied') {
            enqueueSnackbar('Las alertas están bloqueadas en el navegador.', { variant: 'warning' });
        }
    };

    const handleDisableBrowser = () => {
        disableBrowserNotifications();
        refreshBrowserUi();
    };

    const markAll = async () => {
        const response = await postData('notificaciones/leer-todas', {}, usuario.token);
        if (response.error) {
            enqueueSnackbar(response.error, { variant: 'error' });
            return;
        }
        setUnread(0);
        setItems((prev) => prev.map((n) => ({ ...n, leida_at: n.leida_at || new Date().toISOString() })));
    };

    if (!usuario?.token) return null;

    const showBrowserControls = browserUi.supported;
    const browserDenied = browserUi.permission === 'denied';
    const browserActive = browserUi.optedIn && browserUi.permission === 'granted';

    return (
        <>
            <Tooltip title="Notificaciones">
                <IconButton color="inherit" onClick={handleOpen} size="large" aria-label="notificaciones">
                    <Badge badgeContent={unread} color="error" max={99}>
                        {unread > 0 ? <NotificationsActiveIcon /> : <NotificationsNoneIcon />}
                    </Badge>
                </IconButton>
            </Tooltip>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                slotProps={{ paper: { sx: { width: 360, maxWidth: '92vw' } } }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Box sx={{ px: 2, py: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Notificaciones</Typography>
                    <Button size="small" onClick={markAll} disabled={unread === 0}>Marcar todas</Button>
                </Box>
                {showBrowserControls && (
                    <>
                        <Divider />
                        <Box sx={{ px: 2, py: 1 }}>
                            {browserDenied ? (
                                <Typography variant="caption" color="text.secondary">
                                    Las alertas están bloqueadas en el navegador. Corresponde habilitarlas en la configuración del sitio.
                                </Typography>
                            ) : browserActive ? (
                                <>
                                    <Button size="small" onClick={handleDisableBrowser} fullWidth>
                                        Desactivar alertas del navegador
                                    </Button>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                        Las alertas del sistema se muestran cuando la pestaña está en segundo plano, mientras la sesión sigue activa.
                                    </Typography>
                                </>
                            ) : (
                                <>
                                    <Button size="small" onClick={handleEnableBrowser} fullWidth>
                                        Activar alertas del navegador
                                    </Button>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                        Se mostrarán al pasar a segundo plano, mientras la sesión sigue activa.
                                    </Typography>
                                </>
                            )}
                        </Box>
                    </>
                )}
                <Divider />
                <List dense sx={{ maxHeight: 420, overflow: 'auto', py: 0 }}>
                    {items.length === 0 ? (
                        <Box sx={{ p: 2 }}>
                            <Typography variant="body2" color="text.secondary">No hay notificaciones.</Typography>
                        </Box>
                    ) : (
                        items.map((n) => (
                            <ListItemButton
                                key={n.id}
                                onClick={() => goFromNotification(n)}
                                sx={{ bgcolor: n.leida_at ? 'transparent' : 'action.hover' }}
                            >
                                <ListItemText
                                    primary={n.titulo}
                                    secondary={n.cuerpo}
                                    slotProps={{
                                        primary: { fontWeight: n.leida_at ? 500 : 700, variant: 'body2' },
                                        secondary: { variant: 'caption' },
                                    }}
                                />
                            </ListItemButton>
                        ))
                    )}
                </List>
            </Menu>
        </>
    );
}
